import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Modal, SettingsTileCard, ToggleField } from "@fawkes/ui";
import { Megaphone } from "lucide-react";

import type { TileComponentProps } from "./types";

// Advertentie — op welk platform de advertenties terechtkomen. Asielen van de Dierenbescherming
// publiceren op ikzoekbaas.nl, andere op DierMatch.nl, en sommige op allebei; de live preview bij het
// dier volgt deze keuze.
//
// Organisatiecontext (maintainer 2026-08-23). Dit stond per vestiging, maar alle asielen van een
// backoffice publiceren op hetzelfde platform: per vestiging betekende in de praktijk dezelfde keuze
// drieëntwintig keer maken, en één vergeten asiel bleef stil op het verkeerde platform staan.
export const ADVERTENTIE_TILE_KEY = "advertentie";

type Kanaal = "DIERMATCH" | "IKZOEKBAAS" | "BEIDE";

const KANALEN: Kanaal[] = ["DIERMATCH", "IKZOEKBAAS", "BEIDE"];

export function AdvertentieTile({ context }: TileComponentProps) {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const [keuze, setKeuze] = useState<Kanaal | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const { data, refetch } = useCustom<{
    kanaal: Kanaal;
    contactTekst: string | null;
    automatischAfvoeren: boolean;
    geplaatstDagen: number;
    nieuwDagen: number;
  }>({
    url: "herplaatsing/kanaal", method: "get",
  });
  const huidig = data?.data?.kanaal ?? "DIERMATCH";
  // De tekst die op ikzoekbaas onder "Neem contact op" komt. Elke opvang heeft eigen openingstijden en
  // afspraken, dus die zin hoort van hen te zijn en niet van ons. Onder "Ken onze tarieven" staat daar
  // alleen een verwijzing naar de tarievenpagina — daar valt niets in te vullen.
  const [contact, setContact] = useState("");
  // Wanneer een dier vanzelf van de site gaat. Erop zetten blijft handwerk — dat is de andere regel.
  const [afvoeren, setAfvoeren] = useState(true);
  const [dagen, setDagen] = useState("7");
  const [nieuwDagen, setNieuwDagen] = useState("14");
  useEffect(() => {
    if (open) {
      setKeuze(huidig);
      setContact(data?.data?.contactTekst ?? "");
      setAfvoeren(data?.data?.automatischAfvoeren ?? true);
      setDagen(String(data?.data?.geplaatstDagen ?? 7));
      setNieuwDagen(String(data?.data?.nieuwDagen ?? 14));
    }
  }, [open, huidig, data]);

  const { mutate: bewaar, isLoading: bezig } = useCustomMutation();

  if (context.type !== "organization") return null;

  return (
    <>
      <SettingsTileCard
        tileKey={ADVERTENTIE_TILE_KEY}
        icon={<Megaphone className="settings-tile-icon" width={16} height={16} />}
        title={t("instellingen.tile.advertentie.title")}
        description={t("instellingen.tile.advertentie.desc")}
        items={[t(`instellingen.advertentie.kanaal.${huidig}`)]}
        openLabel={t("instellingen.cta.open")}
        onOpen={() => setOpen(true)}
      />
      {open && (
        <Modal
          open
          title={t("instellingen.tile.advertentie.title")}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button type="button" className="fawkes-fin__link" onClick={() => setOpen(false)}>
                {t("common.cancel")}
              </button>
              <Button
                variant="primary"
                disabled={bezig || keuze == null}
                onClick={() => {
                  setFout(null);
                  bewaar(
                    {
                      url: "herplaatsing/kanaal",
                      method: "put",
                      values: {
                        kanaal: keuze,
                        contactTekst: contact,
                        automatischAfvoeren: afvoeren,
                        geplaatstDagen: Number(dagen) || 0,
                        nieuwDagen: Number(nieuwDagen) || 0,
                      },
                    },
                    {
                      onSuccess: () => { void refetch(); setOpen(false); },
                      onError: () => setFout(t("instellingen.advertentie.fout")),
                    },
                  );
                }}
              >
                {t("common.save")}
              </Button>
            </>
          }
        >
          <p className="voorkeuren__hint">{t("instellingen.advertentie.uitleg")}</p>
          <div className="instellingen-advertentie">
            {KANALEN.map((k) => (
              <label key={k} className={keuze === k ? "instellingen-kanaal is-gekozen" : "instellingen-kanaal"}>
                <input
                  type="radio" name="advertentie-kanaal" value={k}
                  checked={keuze === k}
                  onChange={() => { setKeuze(k); setFout(null); }}
                />
                <span>
                  <strong>{t(`instellingen.advertentie.kanaal.${k}`)}</strong>
                  <small>{t(`instellingen.advertentie.kanaal.${k}.uitleg`)}</small>
                </span>
              </label>
            ))}
          </div>
          {keuze !== "DIERMATCH" && (
            /* Alleen relevant zodra er op ikzoekbaas gepubliceerd wordt; daar staan deze twee blokken. */
            <div className="instellingen-advertentie-teksten">
              <label className="fui-field">
                <span className="fui-field__label">{t("instellingen.advertentie.contactTekst")}</span>
                <textarea
                  className="fui-field__input" rows={2} value={contact}
                  placeholder={t("instellingen.advertentie.contactHint")}
                  onChange={(e) => setContact(e.target.value)}
                />
              </label>
            </div>
          )}
          {/* Hoe lang een dier op de site staat. Erop zetten blijft handwerk — een dier verschijnt
              alleen als een medewerker het publiceert; deze twee vensters gaan over wat daarna vanzelf
              gebeurt. */}
          <div className="instellingen-advertentie-teksten">
            <label className="fui-field">
              <span className="fui-field__label">{t("instellingen.advertentie.nieuwDagen")}</span>
              <input
                type="number" min={0} max={90} className="fui-field__input"
                value={nieuwDagen}
                onChange={(e) => setNieuwDagen(e.target.value)}
              />
              <small className="voorkeuren__hint">
                {t(nieuwDagen === "0"
                  ? "instellingen.advertentie.nieuwDagenNul"
                  : "instellingen.advertentie.nieuwDagenUitleg")}
              </small>
            </label>
            <ToggleField
              checked={afvoeren}
              onChange={(e) => setAfvoeren(e.target.checked)}
              label={t("instellingen.advertentie.afvoeren")}
            />
            <p className="voorkeuren__hint">{t("instellingen.advertentie.afvoerenUitleg")}</p>
            <label className="fui-field">
              <span className="fui-field__label">{t("instellingen.advertentie.geplaatstDagen")}</span>
              <input
                type="number" min={0} max={90} className="fui-field__input"
                value={dagen} disabled={!afvoeren}
                onChange={(e) => setDagen(e.target.value)}
              />
              <small className="voorkeuren__hint">
                {t(dagen === "0"
                  ? "instellingen.advertentie.geplaatstDagenNul"
                  : "instellingen.advertentie.geplaatstDagenUitleg")}
              </small>
            </label>
          </div>
          {fout && <p className="fawkes-inboeken__field-error" role="alert">{fout}</p>}
        </Modal>
      )}
    </>
  );
}
